/**
 * Unified Authentication Manager
 * Centralized authentication logic for DataHub
 */

import { errorHandler } from './errorHandler';

// Token types
export const TOKEN_TYPES = {
    SERVICE: 'serviceToken',
    USER: 'user_token',
    AUTH: 'auth_token',
    FIREBASE: 'firebase_token'
};

// Auth states
export const AUTH_STATES = {
    LOADING: 'loading',
    AUTHENTICATED: 'authenticated',
    UNAUTHENTICATED: 'unauthenticated',
    EXPIRED: 'expired',
    ERROR: 'error'
};

class AuthManager {
    constructor() {
        this.listeners = new Set();
        this.authState = AUTH_STATES.LOADING;
        this.currentUser = null;
        this.token = null;
        this.tokenType = null;
        this.refreshTimer = null;

        // Initialize auth state
        this.initializeAuth();
    }

    /**
     * Initialize authentication state
     */
    initializeAuth() {
        try {
            const token = this.getToken();
            if (token) {
                this.token = token;
                this.tokenType = this.getTokenType();
                this.authState = AUTH_STATES.AUTHENTICATED;
                this.scheduleTokenRefresh();
            } else {
                this.authState = AUTH_STATES.UNAUTHENTICATED;
            }
        } catch (error) {
            console.error('Auth initialization error:', error);
            this.authState = AUTH_STATES.ERROR;
        }
    }

    /**
     * Get JWT for API Bearer header. Chỉ trả về token dùng cho Authorization: Bearer.
     * Không trả về user_token (là identifier, không phải JWT).
     */
    getToken() {
        const firebaseToken = localStorage.getItem(TOKEN_TYPES.FIREBASE);
        const serviceToken = localStorage.getItem(TOKEN_TYPES.SERVICE);
        const authToken = localStorage.getItem(TOKEN_TYPES.AUTH);
        return firebaseToken || serviceToken || authToken || null;
    }

    getTokenType() {
        if (localStorage.getItem(TOKEN_TYPES.FIREBASE)) return TOKEN_TYPES.FIREBASE;
        if (localStorage.getItem(TOKEN_TYPES.SERVICE)) return TOKEN_TYPES.SERVICE;
        if (localStorage.getItem(TOKEN_TYPES.AUTH)) return TOKEN_TYPES.AUTH;
        return null;
    }

    /**
     * Set JWT. Chỉ xóa các key JWT cũ (auth, firebase, service), không xóa user_token.
     */
    setToken(token, type = TOKEN_TYPES.AUTH) {
        try {
            this.clearJwtOnly();
            localStorage.setItem(type, token);
            this.token = token;
            this.tokenType = type;
            this.authState = AUTH_STATES.AUTHENTICATED;
            this.scheduleTokenRefresh();
            this.notifyListeners();
        } catch (error) {
            console.error('Error setting token:', error);
            this.authState = AUTH_STATES.ERROR;
            this.notifyListeners();
        }
    }

    /** Chỉ xóa JWT (auth_token, firebase_token, serviceToken), giữ user_token. */
    clearJwtOnly() {
        [TOKEN_TYPES.AUTH, TOKEN_TYPES.FIREBASE, TOKEN_TYPES.SERVICE].forEach((key) => {
            localStorage.removeItem(key);
        });
        this.token = null;
        this.tokenType = null;
        this.authState = AUTH_STATES.UNAUTHENTICATED;
        this.currentUser = null;
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        this.notifyListeners();
    }

    /** Logout: xóa toàn bộ token (kể cả user_token). */
    clearTokens() {
        try {
            Object.values(TOKEN_TYPES).forEach((type) => {
                localStorage.removeItem(type);
            });
            this.token = null;
            this.tokenType = null;
            this.authState = AUTH_STATES.UNAUTHENTICATED;
            this.currentUser = null;
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
            this.notifyListeners();
        } catch (error) {
            console.error('Error clearing tokens:', error);
        }
    }

    /**
     * Check if user is authenticated
     * @returns {boolean} Authentication status
     */
    isAuthenticated() {
        const token = this.getToken();
        return token && token.length > 0 && this.authState === AUTH_STATES.AUTHENTICATED;
    }

    /**
     * Get current user
     * @returns {Object|null} Current user object
     */
    getCurrentUser() {
        return this.currentUser;
    }

    /**
     * Set current user
     * @param {Object} user - User object
     */
    setCurrentUser(user) {
        this.currentUser = user;
        this.notifyListeners();
    }

    /**
     * Get authentication state
     * @returns {string} Current auth state
     */
    getAuthState() {
        return this.authState;
    }

    /**
     * Set authentication state
     * @param {string} state - New auth state
     */
    setAuthState(state) {
        this.authState = state;
        this.notifyListeners();
    }

    /**
     * Check if token is expired
     * @param {string} token - Token to check
     * @returns {boolean} Expiration status
     */
    isTokenExpired(token = this.token) {
        if (!token) return true;

        try {
            // For JWT tokens, decode and check expiration
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp < currentTime;
        } catch (error) {
            // If token is not JWT, assume it's valid
            return false;
        }
    }

    /**
     * Schedule token refresh
     */
    scheduleTokenRefresh() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        // Refresh token every 50 minutes (assuming 1 hour expiry)
        this.refreshTimer = setTimeout(
            () => {
                this.refreshToken();
            },
            50 * 60 * 1000
        );
    }

    /**
     * Refresh authentication token
     */
    async refreshToken() {
        try {
            const token = this.getToken();
            if (!token || this.isTokenExpired(token)) {
                this.authState = AUTH_STATES.EXPIRED;
                this.notifyListeners();
                return;
            }

            // TODO: Implement actual token refresh logic
            // For now, just extend the current token
            console.log('🔄 Token refreshed');
        } catch (error) {
            console.error('Token refresh error:', error);
            this.authState = AUTH_STATES.ERROR;
            this.notifyListeners();
        }
    }

    /**
     * Login with credentials
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<Object>} Login result
     */
    async login(email, password) {
        try {
            this.setAuthState(AUTH_STATES.LOADING);

            // TODO: Implement actual login API call
            // For now, simulate login
            const response = await fetch('/api/v1/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                throw new Error('Login failed');
            }

            const data = await response.json();

            // Set token and user
            this.setToken(data.token, TOKEN_TYPES.AUTH);
            this.setCurrentUser(data.user);

            return { success: true, user: data.user };
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'login',
                email
            });

            this.setAuthState(AUTH_STATES.ERROR);
            throw parsedError;
        }
    }

    /**
     * Logout user
     */
    async logout() {
        try {
            // TODO: Implement logout API call
            // await fetch('/api/v1/auth/logout', {
            //   method: 'POST',
            //   headers: {
            //     'Authorization': `Bearer ${this.token}`,
            //   },
            // });

            this.clearTokens();
            this.setCurrentUser(null);

            console.log('👋 User logged out');
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, clear local tokens
            this.clearTokens();
        }
    }

    /**
     * Register new user
     * @param {Object} userData - User registration data
     * @returns {Promise<Object>} Registration result
     */
    async register(userData) {
        try {
            this.setAuthState(AUTH_STATES.LOADING);

            // TODO: Implement actual registration API call
            const response = await fetch('/api/v1/auth/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            });

            if (!response.ok) {
                throw new Error('Registration failed');
            }

            const data = await response.json();

            // Set token and user
            this.setToken(data.token, TOKEN_TYPES.AUTH);
            this.setCurrentUser(data.user);

            return { success: true, user: data.user };
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'register',
                userData
            });

            this.setAuthState(AUTH_STATES.ERROR);
            throw parsedError;
        }
    }

    /**
     * Add auth state change listener
     * @param {Function} listener - Listener function
     */
    addListener(listener) {
        this.listeners.add(listener);
    }

    /**
     * Remove auth state change listener
     * @param {Function} listener - Listener function
     */
    removeListener(listener) {
        this.listeners.delete(listener);
    }

    /**
     * Notify all listeners of auth state change
     */
    notifyListeners() {
        this.listeners.forEach((listener) => {
            try {
                listener({
                    authState: this.authState,
                    user: this.currentUser,
                    token: this.token,
                    tokenType: this.tokenType
                });
            } catch (error) {
                console.error('Error in auth listener:', error);
            }
        });
    }

    /**
     * Get auth headers for API requests
     * @returns {Object} Headers object
     */
    getAuthHeaders() {
        const token = this.getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * Validate current authentication
     * @returns {Promise<boolean>} Validation result
     */
    async validateAuth() {
        try {
            const token = this.getToken();
            if (!token) {
                this.setAuthState(AUTH_STATES.UNAUTHENTICATED);
                return false;
            }

            if (this.isTokenExpired(token)) {
                this.setAuthState(AUTH_STATES.EXPIRED);
                return false;
            }

            // TODO: Implement actual token validation API call
            // For now, assume token is valid
            this.setAuthState(AUTH_STATES.AUTHENTICATED);
            return true;
        } catch (error) {
            console.error('Auth validation error:', error);
            this.setAuthState(AUTH_STATES.ERROR);
            return false;
        }
    }
}

// Create singleton instance
export const authManager = new AuthManager();

// Export for backward compatibility
export default authManager;
